import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ExternalLink, Loader2, MapPin, Pencil, Plus, Printer, X, Zap } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { CurrencyInput } from '@/components/ui/CurrencyInput';
import { MATERIALS, SPECIALIZATIONS } from '../lib/machinesCatalog';
import type { Farm, FarmLocation } from '../types';

const STATUS_LABELS: Record<string, string> = {
  pending_review: 'Aguardando aprovação',
  active:         'Ativa',
  paused:         'Pausada',
  rejected:       'Rejeitada',
};
const STATUS_CHIPS: Record<string, string> = {
  pending_review: 'chip-warning',
  active:         'chip-positive',
  paused:         'chip-neutral',
  rejected:       'chip-negative',
};

interface FarmWithLocation extends Farm {
  location?: FarmLocation | null;
}

interface FarmCosts {
  hourly_machine_cost_cents: number;
  setup_fee_cents:           number;
  filament_markup_pct:       number;
  delivery_offered:          boolean;
  delivery_radius_km:        number | null;
  delivery_fee_base_cents:   number | null;
  delivery_fee_per_km_cents: number | null;
  modeling_organic_fee_cents:    number;
  modeling_parametric_fee_cents: number;
}

// ─── Modal de edição rápida ───────────────────────────────────────────────────

function FarmEditModal({ farm, onClose, onSaved }: {
  farm: FarmWithLocation;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName]               = useState(farm.name);
  const [description, setDescription] = useState(farm.description ?? '');
  const [status, setStatus]           = useState(farm.status);
  const [offersModeling, setOffersModeling] = useState(!!farm.offers_modeling);
  const [costs, setCosts]             = useState<FarmCosts | null>(null);
  const [materials, setMaterials]     = useState<string[]>([]);
  const [specs, setSpecs]             = useState<string[]>([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  useEffect(() => {
    Promise.all([
      supabase
        .from('farm_costs')
        .select('hourly_machine_cost_cents, setup_fee_cents, filament_markup_pct, delivery_offered, delivery_radius_km, delivery_fee_base_cents, delivery_fee_per_km_cents, modeling_organic_fee_cents, modeling_parametric_fee_cents')
        .eq('farm_id', farm.id)
        .maybeSingle(),
      supabase.from('farm_materials').select('material').eq('farm_id', farm.id),
      supabase.from('farm_specializations').select('specialization').eq('farm_id', farm.id),
    ]).then(([costsRes, matRes, specRes]) => {
      setCosts((costsRes.data as FarmCosts | null) ?? {
        hourly_machine_cost_cents: 0,
        setup_fee_cents: 0,
        filament_markup_pct: 0,
        delivery_offered: false,
        delivery_radius_km: null,
        delivery_fee_base_cents: 0,
        delivery_fee_per_km_cents: 0,
        modeling_organic_fee_cents: 0,
        modeling_parametric_fee_cents: 0,
      });
      setMaterials(((matRes.data as { material: string }[]) ?? []).map((m) => m.material));
      setSpecs(((specRes.data as { specialization: string }[]) ?? []).map((s) => s.specialization));
      setLoading(false);
    });
  }, [farm.id]);

  function toggle(list: string[], setList: (v: string[]) => void, value: string) {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  }

  async function handleSave() {
    if (!name.trim()) { setError('O nome da farm é obrigatório.'); return; }
    setSaving(true);
    setError('');
    try {
      const { error: farmErr } = await supabase
        .from('farms')
        .update({
          name: name.trim(),
          description: description.trim() || null,
          status,
          offers_modeling: offersModeling,
        } as never)
        .eq('id', farm.id);
      if (farmErr) {
        // Mensagem amigável do trigger de pausa com serviços abertos
        throw new Error(farmErr.message.includes('serviço(s) em aberto')
          ? farmErr.message.replace(/^.*?Não é possível/, 'Não é possível')
          : farmErr.message);
      }

      if (costs) {
        const { error: costsErr } = await supabase
          .from('farm_costs')
          .upsert({ farm_id: farm.id, ...costs } as never, { onConflict: 'farm_id' });
        if (costsErr) throw new Error(costsErr.message);
      }

      // Materiais: substitui o conjunto inteiro pelo selecionado
      const { error: delMatErr } = await supabase.from('farm_materials').delete().eq('farm_id', farm.id);
      if (delMatErr) throw new Error(delMatErr.message);
      if (materials.length > 0) {
        const { error: insMatErr } = await supabase
          .from('farm_materials')
          .insert(materials.map((m) => ({ farm_id: farm.id, material: m })) as never);
        if (insMatErr) throw new Error(insMatErr.message);
      }

      // Especializações: idem
      const { error: delSpecErr } = await supabase.from('farm_specializations').delete().eq('farm_id', farm.id);
      if (delSpecErr) throw new Error(delSpecErr.message);
      if (specs.length > 0) {
        const { error: insSpecErr } = await supabase
          .from('farm_specializations')
          .insert(specs.map((s) => ({ farm_id: farm.id, specialization: s })) as never);
        if (insSpecErr) throw new Error(insSpecErr.message);
      }

      onSaved();
    } catch (e: any) {
      setError(e.message ?? 'Erro ao salvar alterações.');
      setSaving(false);
    }
  }

  const canToggleStatus = farm.status === 'active' || farm.status === 'paused';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="card w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg text-ink">Editar farm</h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-ink-muted" /></div>
        ) : (
          <>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Nome da farm *</label>
              <input className="input w-full" value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div>
              <label className="block text-xs text-ink-muted mb-1">Descrição</label>
              <textarea className="input w-full" rows={3} value={description}
                onChange={(e) => setDescription(e.target.value)} />
            </div>

            {canToggleStatus && (
              <div>
                <label className="block text-xs text-ink-muted mb-1">Status</label>
                <select className="input w-full" value={status} onChange={(e) => setStatus(e.target.value as Farm['status'])}>
                  <option value="active">Ativa (visível no marketplace)</option>
                  <option value="paused">Pausada (oculta do marketplace)</option>
                </select>
              </div>
            )}

            {costs && (
              <div className="space-y-3 pt-3 border-t border-border">
                <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider">Preços e custos</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-ink-muted mb-1">Custo/hora de máquina</label>
                    <CurrencyInput className="input w-full" value={costs.hourly_machine_cost_cents}
                      onChange={(v) => setCosts({ ...costs, hourly_machine_cost_cents: v })} />
                  </div>
                  <div>
                    <label className="block text-xs text-ink-muted mb-1">Taxa de setup</label>
                    <CurrencyInput className="input w-full" value={costs.setup_fee_cents}
                      onChange={(v) => setCosts({ ...costs, setup_fee_cents: v })} />
                  </div>
                  <div>
                    <label className="block text-xs text-ink-muted mb-1">Markup filamento (%)</label>
                    <input type="number" min={0} step={1} className="input w-full"
                      value={costs.filament_markup_pct}
                      onChange={(e) => setCosts({ ...costs, filament_markup_pct: Number(e.target.value) })} />
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                      <input type="checkbox" checked={costs.delivery_offered}
                        onChange={(e) => setCosts({ ...costs, delivery_offered: e.target.checked })} />
                      Oferece entrega
                    </label>
                  </div>
                  {costs.delivery_offered && (
                    <>
                      <div>
                        <label className="block text-xs text-ink-muted mb-1">Raio de entrega (km)</label>
                        <input type="number" min={0} className="input w-full"
                          value={costs.delivery_radius_km ?? ''}
                          onChange={(e) => setCosts({ ...costs, delivery_radius_km: e.target.value ? Number(e.target.value) : null })} />
                      </div>
                      <div>
                        <label className="block text-xs text-ink-muted mb-1">Taxa base de entrega</label>
                        <CurrencyInput className="input w-full" value={costs.delivery_fee_base_cents ?? 0}
                          onChange={(v) => setCosts({ ...costs, delivery_fee_base_cents: v })} />
                      </div>
                      <div>
                        <label className="block text-xs text-ink-muted mb-1">Taxa por km</label>
                        <CurrencyInput className="input w-full" value={costs.delivery_fee_per_km_cents ?? 0}
                          onChange={(v) => setCosts({ ...costs, delivery_fee_per_km_cents: v })} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Serviço de modelagem 3D */}
            <div className="pt-1 space-y-3">
              <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                <input
                  type="checkbox"
                  checked={offersModeling}
                  onChange={(e) => setOffersModeling(e.target.checked)}
                />
                Ofereço serviço de modelagem 3D (modelo o produto para o cliente)
              </label>
              {offersModeling && costs && (
                <div className="grid grid-cols-2 gap-3 rounded-xl bg-brand-50 border border-brand-200 p-3">
                  <div>
                    <label className="block text-xs text-ink-muted mb-1">Modelagem orgânica (personagens, bonecos)</label>
                    <CurrencyInput className="input w-full" value={costs.modeling_organic_fee_cents}
                      onChange={(v) => setCosts({ ...costs, modeling_organic_fee_cents: v })} />
                  </div>
                  <div>
                    <label className="block text-xs text-ink-muted mb-1">Modelagem paramétrica (CAD)</label>
                    <CurrencyInput className="input w-full" value={costs.modeling_parametric_fee_cents}
                      onChange={(v) => setCosts({ ...costs, modeling_parametric_fee_cents: v })} />
                  </div>
                  <p className="col-span-2 text-[11px] text-ink-soft">
                    Valor adicional cobrado por modelagem, somado ao orçamento quando o cliente solicita o serviço.
                  </p>
                </div>
              )}
            </div>

            {/* Materiais */}
            <div className="pt-3 border-t border-border">
              <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-2">Materiais aceitos</p>
              <div className="flex flex-wrap gap-2">
                {MATERIALS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => toggle(materials, setMaterials, m.value)}
                    className={`chip cursor-pointer transition-colors ${
                      materials.includes(m.value)
                        ? 'bg-brand-700 text-white'
                        : 'bg-surface-subtle text-ink-muted hover:text-ink'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Especializações */}
            <div className="pt-3 border-t border-border">
              <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-2">Especializações</p>
              <div className="flex flex-wrap gap-2">
                {SPECIALIZATIONS.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => toggle(specs, setSpecs, s.value)}
                    className={`chip cursor-pointer transition-colors ${
                      specs.includes(s.value)
                        ? 'bg-brand-700 text-white'
                        : 'bg-surface-subtle text-ink-muted hover:text-ink'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <p className="text-xs text-ink-soft">
              Endereço, máquinas e turnos ainda não são editáveis por aqui.
            </p>

            {error && (
              <div className="flex items-center gap-2 text-xs text-negative bg-negative/10 border border-negative/20 rounded-xl px-3 py-2">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />{error}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? 'Salvando…' : 'Salvar alterações'}
              </button>
              <button onClick={onClose} disabled={saving} className="btn-secondary">Cancelar</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

export function MyFarms() {
  const { tenantId } = useTenant();
  const navigate     = useNavigate();
  const [farms, setFarms]         = useState<FarmWithLocation[]>([]);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activating, setActivating] = useState<string | null>(null);
  const [editing, setEditing]     = useState<FarmWithLocation | null>(null);

  function loadFarms(tid: string) {
    setLoading(true);
    setLoadError(null);
    supabase
      .from('farms')
      .select('*, farm_locations(*)')
      .eq('owner_tenant_id', tid)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) { setLoadError(error.message); setLoading(false); return; }
        const rows = (data as any[] ?? []).map((f) => ({
          ...f,
          location: f.farm_locations?.[0] ?? null,
        })) as FarmWithLocation[];
        setFarms(rows);
        setLoading(false);
      });
  }

  useEffect(() => {
    if (!tenantId) { setLoading(false); return; }
    loadFarms(tenantId);
  }, [tenantId]);

  async function activateFarm(farmId: string) {
    setActivating(farmId);
    await supabase.from('farms').update({ status: 'active' } as never).eq('id', farmId);
    if (tenantId) loadFarms(tenantId);
    setActivating(null);
  }

  return (
    <PageShell title="Minhas farms" subtitle="Farms que você cadastrou na plataforma." back="/farms">
      <div className="flex justify-end mb-4">
        <Link to="/farms/cadastrar" className="btn-primary gap-2">
          <Plus className="w-4 h-4" />Nova farm
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2].map((i) => <div key={i} className="h-24 rounded-2xl bg-surface-subtle animate-pulse" />)}
        </div>
      ) : loadError ? (
        <div className="card p-6 flex items-start gap-3 text-negative">
          <AlertCircle className="shrink-0 mt-0.5" size={18} />
          <div>
            <p className="font-medium text-sm">Erro ao carregar farms</p>
            <p className="text-xs mt-1 text-ink-muted">{loadError}</p>
            {tenantId && (
              <button onClick={() => loadFarms(tenantId)} className="text-xs underline mt-2">Tentar novamente</button>
            )}
          </div>
        </div>
      ) : farms.length === 0 ? (
        <div className="card p-8">
          <EmptyState
            icon={Printer}
            title="Nenhuma farm cadastrada"
            description="Cadastre sua farm para aparecer no marketplace e receber pedidos."
            action={<Link to="/farms/cadastrar" className="btn-primary">Cadastrar farm</Link>}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {farms.map((f) => (
            <div
              key={f.id}
              className="card p-5 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/farms/${f.slug}`)}
            >
              <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center text-2xl shrink-0">
                {f.logo_url ? <img src={f.logo_url} alt={f.name} className="w-12 h-12 rounded-xl object-cover" /> : '🖨️'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-ink">{f.name}</p>
                {f.location ? (
                  <p className="text-xs text-ink-muted flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" />{f.location.city}, {f.location.state_uf}
                  </p>
                ) : (
                  <p className="text-xs text-ink-muted truncate mt-0.5">{f.description || 'Sem descrição'}</p>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                <span className={STATUS_CHIPS[f.status] ?? 'chip-neutral'}>{STATUS_LABELS[f.status] ?? f.status}</span>
                {f.status === 'pending_review' && (
                  <button
                    onClick={() => activateFarm(f.id)}
                    disabled={activating === f.id}
                    className="btn-primary !py-1 !text-xs gap-1"
                    title="Publicar farm no marketplace"
                  >
                    <Zap className="w-3.5 h-3.5" />
                    {activating === f.id ? '…' : 'Publicar'}
                  </button>
                )}
                <button
                  onClick={() => setEditing(f)}
                  className="text-ink-muted hover:text-brand-700 transition-colors p-0.5"
                  title="Editar farm"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <a
                  href={`/farms/${f.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-ink-muted hover:text-brand-700 transition-colors p-0.5"
                  title="Ver perfil público"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <FarmEditModal
          farm={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            if (tenantId) loadFarms(tenantId);
          }}
        />
      )}
    </PageShell>
  );
}
